import { Table, TableContainer, Tbody, Td, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatDateTime, useAppToast, useHasRole } from '../../../helpers'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import { SectorCreateModal } from './SectorCreateModal'
import { SectorEditModal } from './SectorEditModal'

export default function SectorsPage() {
  const sectorService = new SectorService()
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [sectors, setSectors] = useState<ISector[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [isSectorCreateModalOpen, setIsSectorCreateModalOpen] = useState(false)
  const { isOpen: isOpenDeleteDialog, onOpen: onOpenDeleteDialog, onClose: onCloseDeleteDialog } = useDisclosure()
  const [modalSector, setModalSector] = useState<ISector | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSectorEditModalOpen, setIsSectorEditModalOpen] = useState(false)
  const hasRole = useHasRole()
  const toast = useAppToast()
  const permissions = {
    addSector: hasRole('add_sector'),
    editSector: hasRole('edit_sector'),
    deleteSector: hasRole('delete_sector')
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      loadSectors(page)
    }, 500)
    return () => clearTimeout(handler)
  }, [page, pageSize])

  const loadSectors = async (page: number) => {
    setLoading(true)
    setLoadError(false)
    try {
      const { total_selected, per_page, data } = await sectorService.list({ page, pageSize })

      setSectors(data)

      setTotalPages(Math.ceil(total_selected / per_page))
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os setores', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCloseSectorCreateModal = () => {
    setIsSectorCreateModalOpen(false)
    loadSectors(page)
  }

  const handleCloseSectorEditModal = () => {
    setIsSectorEditModalOpen(false)
    loadSectors(page)
  }

  const handleDelete = async () => {
    if (!modalSector) {
      return
    }

    setIsDeleting(true)
    try {
      await sectorService.destroy(modalSector.id)
      toast.success('Setor excluído')
      onCloseDeleteDialog()
      loadSectors(page)
    } catch (error) {
      toast.error('Erro ao excluir setor', error)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <PageTitle title="Setores" description="Setores da empresa usados em avaliações e solicitações" />
      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>Nome do Setor</Th>
              <Th>Criado por</Th>
              <Th>Data de Criação</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={4} />
            ) : loadError ? (
              <TableErrorRow colSpan={4} entity="setores" onRetry={() => loadSectors(page)} />
            ) : sectors.length === 0 ? (
              <TableEmptyRow colSpan={4} entity="setor" />
            ) : (
              sectors.map((sector) => (
                <Tr key={sector.id}>
                  <Td>{sector.name}</Td>
                  <Td>{sector.created_by.name}</Td>
                  <Td>{formatDateTime(sector.created_at)}</Td>
                  <Td>
                    {permissions.editSector && (
                      <ActionButton
                        action="edit"
                        onClick={() => {
                          setIsSectorEditModalOpen(true)
                          setModalSector(sector)
                        }}
                      />
                    )}

                    {permissions.deleteSector && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setModalSector(sector)
                          onOpenDeleteDialog()
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
          loadSectors(page)
        }}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size)
          setPage(1)
        }}
      />

      <SectorCreateModal isOpen={isSectorCreateModalOpen} onClose={handleCloseSectorCreateModal} />
      {modalSector && (
        <SectorEditModal isOpen={isSectorEditModalOpen} onClose={handleCloseSectorEditModal} sector={modalSector} />
      )}
      <ConfirmDialog
        isOpen={isOpenDeleteDialog}
        onClose={onCloseDeleteDialog}
        onConfirm={handleDelete}
        action="delete"
        entity="setor"
        name={modalSector?.name}
        isLoading={isDeleting}
      />

      {permissions.addSector && <ActionFab label="Adicionar setor" onClick={() => setIsSectorCreateModalOpen(true)} />}
    </>
  )
}
